import { ReactNode, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { selectUserId } from "fond/api";
import { useSwitchableAccounts } from "fond/hooks/useSwitchableAccounts";
import { NavigateError } from "fond/navigation/NavigateError";
import { selectIsSwitchingAccount } from "fond/redux/accountContextSlice";
import { isNetworkError, ResourceType } from "fond/types";
import { getAccountContext } from "fond/utils/accountContextStorage";
import { useAppSelector } from "fond/utils/hooks";
import { BlockSpinner, NetworkErrorModal } from "fond/widgets";

type EntityWithAccount =
  | {
      Account?: { ID: string; Name: string };
    }
  | null
  | undefined;

interface EntityAccessGuardProps {
  /**
   * The URL parameter name containing the entity ID
   * @example "projectId", "folderId", "reportId"
   */
  paramName: string;

  /**
   * RTK Query hook to fetch the entity directly (non-account-scoped)
   * @example useGetProjectQuery, useGetFolderQuery
   */
  useGetQuery: (
    id: string,
    options?: any
  ) => {
    data?: EntityWithAccount;
    isLoading: boolean;
    isError: boolean;
    error?: any;
    refetch: () => void;
  };

  /**
   * The resource type name for error messages
   * @example "PROJECT", "FOLDER", "REPORT"
   */
  resourceType: ResourceType;

  /**
   * Children to render if access check passes
   */
  children: ReactNode;
}

/**
 * Route guard that validates entity access across accounts.
 *
 * **Responsibilities:**
 * 1. Fetches the entity directly (non-account-scoped) using the provided query hook
 * 2. If entity exists in a different account:
 *    - Checks if user has access to that account
 *    - Redirects to /switchaccount if yes
 *    - Shows 404 if no access
 * 3. Renders children if entity is in current account
 *
 * **Usage:**
 * ```tsx
 * <Route path="/account/:accountId/project/:projectId" element={
 *   <EntityAccessGuard
 *     paramName="projectId"
 *     useGetQuery={useGetProjectQuery}
 *     resourceType="PROJECT"
 *   >
 *     <ProjectPage />
 *   </EntityAccessGuard>
 * } />
 * ```
 *
 * **Why this exists:**
 * With account-scoped queries, entities from other accounts won't be in the cache.
 * This guard fetches the entity directly to check if it exists in a different account
 * the user has access to, enabling seamless account switching.
 *
 * @example
 * // User navigates to: /account/A/project/123 (but project 123 belongs to account B)
 * // 1. Guard fetches project 123 directly
 * // 2. Detects it belongs to account B
 * // 3. Checks if user has access to account B
 * // 4. If yes: redirects to /switchaccount with account B info
 * // 5. If no: shows 404
 */
export const EntityAccessGuard: React.FC<EntityAccessGuardProps> = ({ paramName, useGetQuery, resourceType, children }: EntityAccessGuardProps) => {
  const params = useParams();
  const entityId = params[paramName];
  const userId = useAppSelector(selectUserId);
  const currentAccountId = getAccountContext(userId);
  const isSwitchingAccount = useAppSelector(selectIsSwitchingAccount);
  const { accounts } = useSwitchableAccounts();
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch entity directly (non-account-scoped)
  const {
    data: entity,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetQuery(entityId as string, {
    skip: !entityId,
  });

  useEffect(() => {
    // Skip redirect if an account switch is already in progress (e.g. user confirmed via modal)
    if (isSwitchingAccount) return;

    // Only check if we have an entity that belongs to a different account
    if (!entity?.Account || !currentAccountId) return;

    // Entity belongs to different account than the current context. Note that if the entity
    // was returned by the backend then we can safely imply the user has access (although we check
    // just to be sure).
    if (entity.Account.ID !== currentAccountId) {
      const userHasAccess = accounts.some((account) => account.ID === entity.Account?.ID);

      if (userHasAccess) {
        // User has access to the entity's account - trigger account switch
        navigate("/switchaccount", {
          state: {
            targetAccountId: entity.Account.ID,
            targetAccountName: entity.Account.Name,
            returnPath: location.pathname + location.search,
          },
        });
      }
      // If user doesn't have access, let it fall through to 404
    }
  }, [entity, currentAccountId, isSwitchingAccount, accounts, navigate, location]);

  // Still loading - show spinner
  if (isLoading) {
    return <BlockSpinner />;
  }

  // Check for network errors first (offline, timeout, connection lost)
  // Show modal with retry instead of navigating to 404
  if (isError && isNetworkError(error)) {
    return <NetworkErrorModal onReload={refetch} />;
  }

  // Show 404 if:
  // 1. HTTP error fetching entity (likely 403/404)
  // 2. Entity doesn't exist
  // 3. Entity exists in different account but user has no access (handled by effect above)
  if (isError || !entity) {
    return <NavigateError to="/404" resourceType={resourceType} />;
  }

  // Entity exists and user has access - render children
  return children;
};
